import { Database, Trophy, LineChart, Search, Users, Shield, Zap, Activity, Layers, Radio, ClipboardList, Gauge } from "lucide-react";

export const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Products", to: "/products" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1778957489702-6455b42cf3eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxhdGhsZXRlJTIwcnVubmluZyUyMGRhcmslMjBzdGFkaXVtfGVufDB8fHx8MTc4Mzk4MDI5N3ww&ixlib=rb-4.1.0&q=85",
  dashboard: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBhbmFseXRpY3MlMjBkYXRhfGVufDB8fHx8MTc4Mzk4MDI5N3ww&ixlib=rb-4.1.0&q=85",
  training: "https://images.unsplash.com/photo-1602105320811-f504e5ff5011?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxiYXNrZXRiYWxsJTIwdHJhaW5pbmclMjBneW18ZW58MHx8fHwxNzgzOTgwMjk3fDA&ixlib=rb-4.1.0&q=85",
  architecture: "https://images.unsplash.com/photo-1558389458-536ad3f4334a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxhdGhsZXRlJTIwcnVubmluZyUyMGRhcmslMjBzdGFkaXVtfGVufDB8fHx8MTc4Mzk4MDI5N3ww&ixlib=rb-4.1.0&q=85",
};

export const STATS = [
  { value: "2.4M+", label: "Records Indexed" },
  { value: "180+", label: "Programs Served" },
  { value: "99.9%", label: "Uptime SLA" },
  { value: "48hrs", label: "Avg. Deploy Time" },
];

export const SERVICES = [
  { icon: Database, title: "Custom Data Systems", desc: "Bespoke platforms that collect, structure, and surface athletic data — built around your workflows, not a template.", tags: ["Ingestion", "Schema Design", "APIs"] },
  { icon: Search, title: "Searchable Record Books", desc: "Turn decades of paper archives into instant, filterable digital record books with athlete, event, and season drill-downs.", tags: ["OCR", "Indexing", "History"] },
  { icon: LineChart, title: "Performance Analytics", desc: "Velocity tracking, kinematic sequences, and roster analytics rendered in real-time dashboards coaches actually use.", tags: ["Dashboards", "Trends", "Alerts"] },
  { icon: Shield, title: "Compliance & Governance", desc: "Association-grade data governance, eligibility tracking, and audit trails engineered for state-level oversight.", tags: ["Eligibility", "Audit", "Roles"] },
  { icon: Radio, title: "Live Event Data", desc: "Capture results as they happen with live scoring pipelines that push straight to public leaderboards.", tags: ["Live", "Leaderboards", "Sync"] },
  { icon: Layers, title: "Integrations", desc: "Connect timing systems, scheduling tools, and existing databases into one unified source of truth.", tags: ["Timing", "Scheduling", "ETL"] },
];

export const PRODUCTS = [
  {
    name: "RecordVault",
    tagline: "The searchable record book",
    desc: "A living archive of every school, team, and individual record — searchable by athlete, event, season, and classification. Break down state history in seconds.",
    features: ["Instant fuzzy search", "Classification splits", "Public + private views", "Automated record validation"],
    icon: Trophy,
    accent: "orange",
  },
  {
    name: "TrainLoad",
    tagline: "Training data intelligence",
    desc: "Aggregate load, velocity, and readiness metrics across your roster. Spot injury risk early and periodize with confidence.",
    features: ["Load monitoring", "Readiness scores", "Injury flags", "Position benchmarks"],
    icon: Activity,
    accent: "teal",
  },
  {
    name: "RosterOps",
    tagline: "Eligibility & operations",
    desc: "Eligibility tracking, roster management, and compliance workflows in one governed system built for associations.",
    features: ["Eligibility engine", "Audit trails", "Role-based access", "Bulk imports"],
    icon: ClipboardList,
    accent: "orange",
  },
];

export const CASE_STUDIES = [
  {
    org: "Statewide Athletic Association",
    metric: "2.4M records",
    title: "Digitizing 40 years of state track & field records",
    summary: "We converted four decades of paper record books into a fully searchable public archive, cutting record-lookup time from days to milliseconds.",
    outcomes: ["94% faster record verification", "Public archive with 120k+ monthly searches", "Zero data-entry backlog"],
    sport: "Track & Field",
  },
  {
    org: "Regional Basketball Conference",
    metric: "+31% readiness",
    title: "Cutting soft-tissue injuries with training load intelligence",
    summary: "TrainLoad surfaced acute:chronic workload spikes across 14 programs, giving coaches an early-warning system for injury risk.",
    outcomes: ["31% fewer soft-tissue injuries", "Unified load data across 14 teams", "Weekly readiness reports"],
    sport: "Basketball",
  },
  {
    org: "Multi-Sport Club Network",
    metric: "48hr deploy",
    title: "One source of truth for a 22-club network",
    summary: "We integrated scheduling, timing, and eligibility into a single governed platform deployed across 22 clubs in under a week.",
    outcomes: ["22 clubs unified", "Eligibility disputes down 80%", "Deployed in 48 hours"],
    sport: "Multi-Sport",
  },
];

export const MANIFESTO = [
  { n: "01", title: "Data is the scoreboard behind the scoreboard", body: "Every whistle, split, and rep is a data point. We build the systems that turn that raw signal into decisions coaches trust." },
  { n: "02", title: "Structure beats spreadsheets", body: "Athletic programs drown in disconnected files. We impose structure — clean schemas, real relationships, instant search." },
  { n: "03", title: "Speed is a feature", body: "If a record lookup takes more than a moment, it won't get used. Our platforms are engineered for millisecond answers." },
  { n: "04", title: "Built for the people on the field", body: "Coaches and directors aren't analysts. Our interfaces are intuitive enough for game day and powerful enough for the front office." },
];

export const INTERESTS = ["RecordVault", "TrainLoad", "RosterOps", "Custom Build", "General"];
export const ROLES = ["Coach", "Athletic Director", "State Association", "Club Administrator", "Other"];

// Mock chart data for demo dashboards
export const PERF_TREND = [
  { week: "W1", velocity: 62, load: 40 },
  { week: "W2", velocity: 68, load: 52 },
  { week: "W3", velocity: 65, load: 60 },
  { week: "W4", velocity: 74, load: 58 },
  { week: "W5", velocity: 80, load: 66 },
  { week: "W6", velocity: 78, load: 72 },
  { week: "W7", velocity: 86, load: 70 },
  { week: "W8", velocity: 92, load: 76 },
];

export const RECORDS_TABLE = [
  { event: "100m Dash", athlete: "M. Carter", mark: "10.42s", year: "2023", cls: "5A" },
  { event: "Long Jump", athlete: "J. Reyes", mark: "7.68m", year: "2021", cls: "6A" },
  { event: "1600m", athlete: "A. Novak", mark: "4:08.1", year: "2024", cls: "4A" },
  { event: "Shot Put", athlete: "D. Okafor", mark: "19.2m", year: "2019", cls: "6A" },
  { event: "High Jump", athlete: "S. Lindqvist", mark: "2.14m", year: "2022", cls: "5A" },
];

export const READINESS = [
  { name: "Ready", value: 62, color: "#00F0FF" },
  { name: "Monitor", value: 26, color: "#FF4A1C" },
  { name: "Rest", value: 12, color: "#94A3B8" },
];

export { Zap, Users, Gauge };
